import Spinner from "@/components/Spinner";
import { Button } from "@/components/ui/button";
import { useEffect, useRef, useState } from "react";
import { endpoints } from "../../../hooks/endpoints";
import {
  getToken
} from "../../../hooks/utils";
import { useAppDispatch, useAppSelector } from "../../../store/hooks";
import { invokeUserRefetch } from "../../../store/slices/storeSlice";
import Separator from "../../Separator";
import CodeLessonsAndCats from "./CodeLessonsAndCats";
import { CodesLessonsAndCatsI } from "./EditCode";

type CodeType = "credit" | "discount";

const CreateCode = () => {
  const [isPending, setIsPending] = useState(false);
  const [couponsError, setCouponsError] = useState("");
  const [couponType, setCouponType] = useState<CodeType>("discount");

  const { hostname } = useAppSelector((state) => state.hostname);

  const initialLessons = {};
  const initialCats = {};

  const [lessons, setLessons] = useState<CodesLessonsAndCatsI>(initialLessons);
  const [cats, setCats] = useState<CodesLessonsAndCatsI>(initialCats);

  useEffect(() => {
    dispatch(invokeUserRefetch());
  }, []);

  const countRef = useRef();
  const prefixRef = useRef();
  const creditRef = useRef();

  const dispatch = useAppDispatch();

  // const lessons = portal.data;

  function downloadCouponData(couponsArray) {
    var blob = new Blob([couponsArray.join("\n")], { type: "text/plain" });
    var link = document.createElement("a");
    link.href = URL.createObjectURL(blob);
    link.download = "coupons.txt";
    link.click();
  }

  const getCodes = async (e) => {
    e.preventDefault();
    setIsPending(true);
    setCouponsError("");

    try {
      const res = await fetch(endpoints.generateCodes, {
        headers: {
          "Content-Type": "application/json",
          Authorization: getToken(),
        },
        method: "POST",
        body: JSON.stringify({
          coupon_type: couponType,
          coupon_credit: (creditRef.current as any)?.value,
          number_of_coupons: (countRef.current as any)?.value ?? 5,
          coupon_prefix: (prefixRef.current as any)?.value ?? "",
          included_lessons: Object.keys(lessons).filter(
            (el) => lessons[el].included
          ),
          included_cats: Object.keys(cats).filter((el) => cats[el].included),
          excluded_lessons: Object.keys(lessons).filter(
            (el) => lessons[el].excluded
          ),
          excluded_cats: Object.keys(cats).filter((el) => cats[el].excluded),
        }),
      });

      const response = await res.json();

      if (!res.ok) {
        throw new Error(response.data);
      }

      downloadCouponData(response.data);
      dispatch(invokeUserRefetch());
    } catch (e) {
      setCouponsError(e.message);
    } finally {
      setIsPending(false);
    }
  };

  return (
    <form
      className="p-3 rounded-lg border shadow-sm bg-white"
      onSubmit={(e) => getCodes(e)}
    >
      <h4 className="p-2 mb-4 border-b-2 border-primary w-fit font-bold">
        إنشاء أكواد
      </h4>

      {["azhar-academy.com", "localhost"].includes(hostname) && (
        <div className="mb-3">
          <label htmlFor="coupon-type" className="block font-bold mb-1">
            نوع الكود
          </label>
          <select
            id="coupon-type"
            className="border border-gray-300 rounded-md p-2 w-3/4 mb-2 focus:outline-none focus:ring-2 focus:ring-primary"
            value={couponType}
            onChange={(e) => setCouponType(e.target.value as CodeType)}
          >
            <option value="discount">كود شراء حصة</option>
            <option value="credit">كود شحن رصيد</option>
          </select>
        </div>
      )}

      {couponType === "discount" && (
        <>
          <CodeLessonsAndCats
            type="included"
            itemType="lesson"
            title="حصص يمكن فتحها بالكود"
            items={lessons}
            setItems={setLessons}
          />
          <Separator />
          <CodeLessonsAndCats
            type="included"
            itemType="category"
            title="مجاميع يمكن فتح حصصها بالكود"
            items={cats}
            setItems={setCats}
          />
          <Separator />
          <CodeLessonsAndCats
            type="excluded"
            itemType="lesson"
            title="حصص لا يمكن فتحها بالكود"
            items={lessons}
            setItems={setLessons}
          />
          <Separator />
          <CodeLessonsAndCats
            type="excluded"
            itemType="category"
            title="مجاميع لا يمكن فتح حصصها بالكود"
            items={cats}
            setItems={setCats}
          />
          <Separator />
        </>
      )}

      {couponType === "discount" && (
        <div className="mb-3">
          <label htmlFor="coupon-prefix" className="block font-bold mb-1">
            وضع رمز معين في جميع الأكواد (حرفين فقط)
          </label>
          <input
            ref={prefixRef}
            type="text"
            id="coupon-prefix"
            maxLength={2}
            placeholder="XX"
            className="border border-gray-300 rounded-md p-2 w-3/4 mb-2 focus:outline-none focus:ring-2 focus:ring-primary"
          />
        </div>
      )}

      {couponType === "credit" && (
        <div className="mb-3">
          <label htmlFor="coupon-credit" className="block font-bold mb-1">
            قيمة الكود بالجنيه
          </label>
          <input
            ref={creditRef}
            type="number"
            id="coupon-credit"
            className="border border-gray-300 rounded-md p-2 w-3/4 mb-2 focus:outline-none focus:ring-2 focus:ring-primary"
          />
        </div>
      )}

      <div className="mb-3">
        <label htmlFor="amount-of-codes" className="block font-bold mb-1">
          عدد الأكواد (بحد أقصى 500 كود)
        </label>
        <input
          ref={countRef}
          type="number"
          id="amount-of-codes"
          max={500}
          min={1}
          placeholder="عدد الأكواد"
          className="border border-gray-300 rounded-md p-2 w-3/4 mb-2 focus:outline-none focus:ring-2 focus:ring-primary"
        />
      </div>

      <div className="flex items-center gap-3">
        <Button>{isPending ? <Spinner variant="button" /> : "انشاء"}</Button>
        <p className="inline text-red-600 my-auto">سعر الكود 5 جنيه</p>
      </div>

      {!!couponsError && (
        <div
          className="bg-red-100 text-red-700 rounded-lg px-4 py-2 mt-3 w-fit"
          role="alert"
        >
          {couponsError}
        </div>
      )}
    </form>
  );
};

export default CreateCode;
